import ClearIcon from "@mui/icons-material/Clear";
import SearchIcon from "@mui/icons-material/Search";
import { Box, Chip, Fade, FormControl, IconButton, InputAdornment, MenuItem, Paper, Select, Stack, Tab, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, Tabs, TextField, Typography, useMediaQuery } from "@mui/material";
import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useWatchInvoiceNotifications } from "../../../notifications/hooks/useWatchInvoiceNotifications";
import { useWatchQuoteAcceptedNotifications } from "../../../notifications/hooks/useWatchQuoteAcceptedNotifications";
import { selectService } from "../../../redux/features/jobCreateSlice";
import { menuPropsModal } from "../../../utils";
import { CalendarDateSelector } from "../../CalendarDateSelector";
import { PaginationLoadingOverlay } from "../../clients/PaginationLoadingOverlay";
import { colors, alphaVar } from "../../ui/colors";
import { useQuotes } from "../hooks/useQuotes";
import { QuoteRow } from "./QuoteRow";
import { QuoteFilters } from "./filters/QuoteFilters";

const GRAY = colors.gray;
const ORANGE = colors.orange;

function getWorkTypeName(id) {
  const names = {
    1: "Office Cleaning",
    2: "Window Cleaning",
    3: "Restaurant Deep Clean",
    4: "Residential Cleaning",
    5: "Post Construction",
  };
  return names[id] ?? "General Service";
}

function computeQuoteDisplayFields(quote) {
  if (!quote) return { workTypeName: "General Service", grossProfit: "—", totalCost: "—", alias: "" };

  const isBasic = quote.type === "basic";

  let totalCost = 0;

  if (isBasic) {
    // For basic quotes, sum up service totals
    totalCost = quote.services?.reduce((acc, s) => acc + (s?.total || 0), 0) || 0;
  } else {
    // For advanced quotes, try grandServiceCostCalc first, then calculate from services
    if (quote.grandServiceCostCalc != null) {
      totalCost = quote.grandServiceCostCalc;
    } else if (quote.services && Array.isArray(quote.services)) {
      // Calculate from services - advanced services have scopeOfWork with costOverride or total
      totalCost = quote.services.reduce((acc, s) => {
        const serviceTotal = s?.scopeOfWork?.costOverride?.value || s?.scopeOfWork?.total || s?.total || 0;
        return acc + Number(serviceTotal);
      }, 0);
    } else {
      totalCost = "—";
    }
  }

  // Aliases are set per service; dedupe to avoid repeating the same tag across multiple services.
  const aliases = Array.from(new Set((quote.services ?? []).map((s) => (typeof s?.alias === "string" ? s.alias.trim() : "")).filter(Boolean)));

  return {
    workTypeName: isBasic
      ? quote.services
          ?.map((s) => s?.service)
          .filter(Boolean)
          .join(", ")
      : (quote.services?.[0]?.scopeOfWork?.typeOfService?.name ?? getWorkTypeName(quote.workScopeId)),
    grossProfit: isBasic ? "—" : quote.grossProfit != null ? `${quote.grossProfit}%` : "—",
    totalCost,
    alias: aliases.join(" • "),
  };
}

const FILTER_PLACEHOLDERS = {
  id: "Search by quote ID...",
  clientName: "Search by client name...",
};

function filterQuotesByField(quotes, filterBy, searchValue) {
  const term = searchValue?.toLowerCase()?.trim() ?? "";
  if (!term || !quotes) return quotes;

  return quotes.filter((quote) => {
    if (filterBy === "id") {
      return String(quote?.id ?? "")
        .toLowerCase()
        .includes(term);
    }

    if (filterBy === "clientName") {
      const client = quote?.client;
      const clientCompanyName = String(client?.clientCompanyName ?? "").toLowerCase();
      const firstName = client?.firstName ?? "";
      const lastName = client?.lastName ?? "";
      const fullName = `${firstName} ${lastName}`.trim().toLowerCase();

      return clientCompanyName.includes(term) || fullName.includes(term);
    }

    return true;
  });
}

// ─── Filter helpers ──────────────────────────────────────────────────────────

function applyAdvancedFilters(quotes, filters) {
  if (!quotes) return quotes;

  let result = [...quotes];

  if (filters?.status && filters.status !== "ALL") {
    result = result.filter((q) => q?.status?.toLowerCase() === filters.status.toLowerCase());
  }

  return result;
}

// ─── Sub-components ──────────────────────────────────────────────────────────
const searchBarPlainStyles = {
  minWidth: 280,
  maxWidth: 360,
  width: "100%",
  "& .MuiOutlinedInput-root": {
    bgcolor: "var(--fx-surface-base, #FFFFFF)",
    borderRadius: "16px",
    minHeight: 36,

    "& fieldset": {
      borderColor: "var(--fx-surface-border, #E2E8F0)",
    },
    "&:hover fieldset": {
      borderColor: "var(--fx-surface-borderstrong, #CBD5E1)",
    },
    "&.Mui-focused fieldset": {
      borderColor: colors.orange[400],
    },

    "& .MuiInputBase-input": {
      fontSize: "0.85rem",
      fontWeight: 500,
    },
  },
};

// ─── Componente de búsqueda tipo Invoice ────────────────────────────────────
function QuoteSearchBar({ filterBy, onFilterChange, searchValue, onSearchChange, onClear, inputRef }) {
  return (
    <Stack direction="row" spacing={1} alignItems="center" sx={{ width: "100%" }}>
      <TextField
        inputRef={inputRef}
        size="small"
        placeholder={FILTER_PLACEHOLDERS?.[filterBy] ?? "Search..."}
        value={searchValue}
        onChange={onSearchChange}
        sx={searchBarPlainStyles}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ color: colors.gray[400], fontSize: 18, ml: 0.5 }} />
            </InputAdornment>
          ),
          endAdornment: !!searchValue && (
            <Fade in>
              <InputAdornment position="end">
                <IconButton size="small" onClick={onClear} sx={{ p: 0.25, mr: 0.5 }}>
                  <ClearIcon sx={{ fontSize: 16, color: colors.gray[400] }} />
                </IconButton>
              </InputAdornment>
            </Fade>
          ),
        }}
      />
      <FormControl size="small">
        <Select
          MenuProps={menuPropsModal}
          value={filterBy}
          onChange={onFilterChange}
          sx={{
            height: 36,
            bgcolor: "var(--fx-surface-base, #FFFFFF)",
            borderRadius: "12px",
            fontSize: "0.8125rem",
            fontWeight: 600,
            color: "text.secondary",
            "& .MuiOutlinedInput-notchedOutline": { borderColor: "var(--fx-surface-border, #E2E8F0)" },
            "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "var(--fx-surface-borderstrong, #CBD5E1)" },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: colors.orange[400] },
          }}
        >
          <MenuItem value="id">Search by ID</MenuItem>
          <MenuItem value="clientName">Search by Client</MenuItem>
        </Select>
      </FormControl>
    </Stack>
  );
}

function QuoteTableHead({ isMobile, compact = false }) {
  const cellSx = {
    fontWeight: 800,
    color: "var(--fx-surface-texttertiary, #94A3B8)",
    borderBottom: "1px solid var(--fx-surface-border, #E2E8F0)",
    fontSize: "0.625rem",
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    padding: compact ? "8px 3px" : "12px 4px",
    lineHeight: 1,
    whiteSpace: "nowrap",
  };

  return (
    <TableHead>
      <TableRow sx={{ backgroundColor: "var(--fx-surface-base, #FFFFFF)", "& th": cellSx }}>
        <TableCell sx={{ width: compact ? 28 : 40, padding: "8px 3px" }} />
        <TableCell sx={{ width: compact ? 42 : 50, padding: "8px 3px" }}>ID</TableCell>

        {isMobile ? (
          <>
            <TableCell sx={{ minWidth: "70px", padding: "8px 4px" }}>Client</TableCell>
            <TableCell sx={{ width: "75px", padding: "8px 4px" }}>Status</TableCell>
            <TableCell sx={{ width: "70px", padding: "8px 4px" }}>Details</TableCell>
          </>
        ) : (
          <>
            <TableCell sx={{ width: compact ? 95 : 160, padding: "8px 3px" }}>Client</TableCell>
            <TableCell sx={{ width: compact ? 70 : 140, padding: "8px 3px" }}>Alias</TableCell>
            <TableCell sx={{ width: compact ? 150 : 180, padding: "8px 3px" }}>Location</TableCell>

            <TableCell sx={{ width: compact ? 58 : 100, padding: "8px 3px" }} align="center">
              Deposit
            </TableCell>

            <TableCell sx={{ width: compact ? 68 : 100, padding: "8px 3px" }} align="right">
              Total
            </TableCell>

            <TableCell sx={{ width: compact ? 78 : 120, padding: "8px 3px" }}>Status</TableCell>

            <TableCell sx={{ width: compact ? 56 : 90, padding: "8px 3px" }}>Date</TableCell>

            <TableCell sx={{ width: compact ? 34 : 48, padding: "8px 3px" }} />
          </>
        )}
      </TableRow>
    </TableHead>
  );
}

// ─── Main Component ──────────────────────────────────────────────────────────

export function QuoteTable({ enableSelection = false, onlyAccepted = false }) {
  const { getAllQuotes, quotes, loading, error } = useQuotes();
  const { selectedService } = useSelector((state) => state.jobCreateStepper);
  const dispatch = useDispatch();

  const isMobile = useMediaQuery("(max-width:600px)");
  const searchInputRef = useRef(null);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filterBy, setFilterBy] = useState("id");
  const [searchValue, setSearchValue] = useState("");

  // Advanced filters
  const [statusFilter, setStatusFilter] = useState(undefined);
  const [depositFilter, setDepositFilter] = useState(undefined);
  const [dateRange, setDateRange] = useState({ start: null, end: null });
  const [showArchived, setShowArchived] = useState(false);

  const selectionEnabled = enableSelection && onlyAccepted;

  useWatchInvoiceNotifications({
    dissableSnackbarAlert: true,
    onNotificationArrived: () => {
      if (typeof window !== "undefined") {
        window.dispatchEvent(new Event("flownect:quotes-updated"));
      }
    },
  });

  useWatchQuoteAcceptedNotifications({
    dissableSnackbarAlert: false,
    onNotificationArrived: () => {
      if (typeof window !== "undefined") {
        window.dispatchEvent(new Event("flownect:quotes-updated"));
      }
    },
  });

  useEffect(() => {
    // Client-side pagination + advanced filters need the full dataset, so request a large
    // page size up-front (server caps it) instead of relying on the default 10.
    getAllQuotes(onlyAccepted ? "approved" : null, {
      page: 1,
      pageSize: 1000,
      archivedOnly: showArchived,
      includeArchived: false,
    });
    if (!onlyAccepted) dispatch(selectService(null));
  }, [onlyAccepted, showArchived]);

  const filteredItems = useMemo(() => {
    let base = onlyAccepted ? (quotes?.items ?? []).filter((q) => Array.isArray(q?.services) && q.services.length > 0) : quotes?.items;

    // When selecting quotes for job creation, filter by deposit status
    if (onlyAccepted && base) {
      base = base.filter((q) => {
        // Allow quotes without deposit requirement
        if (!q.depositAmount) return true;
        // Allow quotes with paid deposits
        if (q.depositAmount && q.depositInvoice?.paidAt) return true;
        // Block quotes with unpaid deposits
        return false;
      });
    }

    // Apply search filter
    const searchFiltered = filterQuotesByField(base, filterBy, searchValue);

    // Apply advanced filters
    const advancedFiltered = applyAdvancedFilters(searchFiltered, {
      status: statusFilter,
      dateRange,
      deposit: depositFilter,
    });

    return advancedFiltered;
  }, [quotes?.items, onlyAccepted, filterBy, searchValue, statusFilter, dateRange, depositFilter]);

  const visibleRows = useMemo(() => filteredItems?.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage), [filteredItems, page, rowsPerPage]);

  const handlePageChange = (_, newPage) => setPage(newPage);

  const handleRowsPerPageChange = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = (e) => {
    setSearchValue(e.target.value);
    setPage(0);
  };

  const handleClearSearch = () => {
    setSearchValue("");
    searchInputRef.current?.focus();
  };

  const handleFilterChange = (e) => {
    setFilterBy(e.target.value);
    setPage(0);
  };

  const handleStatusChange = (val) => {
    setStatusFilter(val);
    setPage(0);
  };

  const handleDepositFilterChange = (val) => {
    setDepositFilter(val);
    setPage(0);
  };

  const handleDateRangeChange = (range) => {
    setDateRange(range);
    setPage(0);
  };

  if (error) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "60vh", flexDirection: "column" }}>
        <Typography variant="h6" color={colors.danger.base} sx={{ fontWeight: 600 }}>
          Error loading quotes
        </Typography>
        <Typography variant="body2" sx={{ color: GRAY[400] }}>
          Please try again later.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%", height: "100%", display: "flex", position: "relative", flexDirection: "column", p: 2 }}>
      <AnimatePresence>
        {selectionEnabled && selectedService && (
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            transition={{ duration: 0.6, type: "spring" }}
            style={{ position: "absolute", top: 12, zIndex: 2000, display: "flex", justifyContent: "center", width: "100%", pointerEvents: "none" }}
          >
            <Chip
              sx={{
                pointerEvents: "auto",
                bgcolor: colors.orange[100],
                color: colors.gray[900],
                boxShadow: "0 4px 10px rgba(0,0,0,0.15)",
                fontWeight: 600,
                fontSize: { xs: "0.75rem", sm: "0.85rem", md: "0.9rem" },
                px: 1.5,
                width: { xs: "90%", sm: "70%", md: "50%" },
              }}
              label={`Service Selected: ${selectedService?.service?.scopeOfWork?.typeOfService?.name ?? selectedService?.service?.service ?? "Service"}`}
              onDelete={() => dispatch(selectService(null))}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Filters Section */}
      <Box sx={{ mb: 2, width: "100%" }}>
        <QuoteFilters
          statusFilter={statusFilter}
          onStatusChange={handleStatusChange}
          depositFilter={depositFilter}
          onDepositFilterChange={handleDepositFilterChange}
          hideFilters={selectionEnabled}
        />
      </Box>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            md: "minmax(0, 1fr) 320px",
            lg: "minmax(0, 1fr) 360px",
            xl: "minmax(0, 1fr) 270px",
          },
          columnGap: 2,
          rowGap: 2,
          alignItems: "stretch",

          flex: 1,
          minHeight: 0,
        }}
      >
        {/* Main table section */}
        <Box
          sx={{
            minWidth: 0,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              mb: 2,
              width: "100%",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: 1,
            }}
          >
            {!selectionEnabled && (
              <Tabs
                value={showArchived ? 1 : 0}
                onChange={(_, v) => {
                  setShowArchived(v === 1);
                  setPage(0);
                }}
                sx={{
                  minHeight: 40,
                  "& .MuiTab-root": {
                    textTransform: "none",
                    fontWeight: 800,
                    fontSize: "0.8125rem",
                    minHeight: 40,
                    color: "var(--fx-surface-texttertiary, #94A3B8)",
                  },
                  "& .Mui-selected": { color: `${ORANGE.base} !important` },
                  "& .MuiTabs-indicator": { bgcolor: ORANGE.base, height: 3, borderRadius: "3px 3px 0 0" },
                }}
              >
                <Tab label="Active quotes" disableRipple />
                <Tab label="Archived" disableRipple />
              </Tabs>
            )}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, ml: selectionEnabled ? "auto" : undefined }}>
              <QuoteSearchBar
                filterBy={filterBy}
                onFilterChange={handleFilterChange}
                searchValue={searchValue}
                onSearchChange={handleSearchChange}
                onClear={handleClearSearch}
                inputRef={searchInputRef}
              />
            </Box>
          </Box>

          <TableContainer
            component={Paper}
            sx={{
              display: "flex",
              flexDirection: "column",
              border: "1px solid var(--fx-surface-border, #E2E8F0)",
              backgroundColor: "var(--fx-surface-base, #FFFFFF)",
              borderRadius: 4,
              boxShadow: "0px 4px 20px rgba(0,0,0,0.03)",
              position: "relative",
              overflow: "hidden",
            }}
          >
            <PaginationLoadingOverlay isVisible={loading} />

            <Box
              sx={{
                width: "100%",
                minWidth: 0,
                flex: "0 1 auto",
                maxHeight: 370,
                minHeight: 400,
                overflowX: "auto",
                overflowY: "auto",
              }}
            >
              <Table stickyHeader size="small">
                <QuoteTableHead isMobile={isMobile} compact={selectionEnabled} />
                <TableBody>
                  {visibleRows?.length > 0 ? (
                    visibleRows.map((quote) => {
                      const computed = computeQuoteDisplayFields(quote);

                      return (
                        <QuoteRow
                          key={quote.id}
                          quote={{ ...quote, __computed: computed }}
                          workType={{
                            id: quote.workScopeId,
                            name: computed.workTypeName,
                            type: quote.workScopeId < 4 ? "commercial" : "residential",
                          }}
                          enableSelection={selectionEnabled}
                          isMobile={isMobile}
                          lockDepositAddActions={selectionEnabled}
                        />
                      );
                    })
                  ) : (
                    <TableRow>
                      <TableCell colSpan={isMobile ? 3 : 10} align="center" sx={{ py: 6, borderBottom: "none" }}>
                        <Typography variant="body1" sx={{ color: GRAY[400], fontWeight: 500 }}>
                          {searchValue.trim() ? "No quotes match your search" : onlyAccepted ? "No Accepted Quotes Found" : "No Quotes Found"}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </Box>

            <Box
              sx={{
                position: "sticky",
                bottom: 0,
                bgcolor: "var(--fx-surface-base, #FFFFFF)",
                borderTop: "1px solid rgba(var(--fx-surface-textrgb, 15, 23, 42), 0.06)",
                minHeight: 38,

                display: "flex",
                alignItems: "center",
              }}
            >
              <TablePagination
                rowsPerPageOptions={[5, 10, 20]}
                component="div"
                count={filteredItems?.length ?? 0}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handlePageChange}
                onRowsPerPageChange={handleRowsPerPageChange}
                showFirstButton
                showLastButton
                SelectProps={{
                  MenuProps: menuPropsModal,
                }}
                sx={{
                  width: "100%",
                  minHeight: 38,
                  overflow: "hidden",

                  "& .MuiTablePagination-toolbar": {
                    minHeight: 38,
                    height: 38,
                    px: 1,
                    py: 0,
                    alignItems: "center",
                  },

                  "& .MuiTablePagination-spacer": {
                    flex: 1,
                  },

                  "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
                    fontSize: "0.72rem",
                    color: "var(--fx-surface-textsecondary, #64748B)",
                    fontWeight: 600,
                    m: 0,
                  },

                  "& .MuiTablePagination-select": {
                    fontSize: "0.72rem",
                    py: 0,
                    minHeight: 24,
                  },

                  "& .MuiTablePagination-input": {
                    mr: 1,
                    ml: 0.5,
                  },

                  "& .MuiSvgIcon-root": {
                    fontSize: 18,
                  },

                  "& .MuiIconButton-root": {
                    color: ORANGE.base,
                    width: 28,
                    height: 28,
                    p: 0.25,
                    mx: 0.1,
                    "&:hover": {
                      backgroundColor: alphaVar(ORANGE.base, 0.08),
                    },
                  },

                  "& .MuiIconButton-root.Mui-disabled": {
                    color: "#CBD5E1",
                  },

                  "& .MuiTablePagination-actions": {
                    ml: 0.5,
                  },
                }}
              />
            </Box>
          </TableContainer>
        </Box>

        {/* Calendar section */}
        <Box
          sx={{
            width: "full",
            top: 16,
          }}
        >
          <CalendarDateSelector onFilter={handleDateRangeChange} width="100%" modes={["day", "week", "month"]} />
        </Box>
      </Box>
    </Box>
  );
}
